import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Stack,
  Divider
} from '@mui/material'
import {
  TrendingUp,
  CheckCircle,
  Timer,
  Star,
  Assessment,
  FileDownload,
  Refresh,
  Notifications
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

import KPICard from './KPICard'
import SellersTable from './SellersTable'
import {
  getAllSellersPerformance,
  getSellerPerformance,
  SellerSummary,
  SellerPerformance
} from '../../services/sellerAnalyticsService'
import { exportToCSV, generateSellerAnalyticsPDF } from '../../services/exportService'
import { db } from '../../firebase'
import { collection, getDocs, query, where } from 'firebase/firestore'

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8']

export default function SellerAnalyticsPage() {
  // State
  const [loading, setLoading] = useState(false)
  const [sellers, setSellers] = useState<SellerSummary[]>([])
  const [selectedSeller, setSelectedSeller] = useState<string>('')
  const [sellerPerformance, setSellerPerformance] = useState<SellerPerformance | null>(null)
  const [startDate, setStartDate] = useState<Date>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) // Last 30 days
  )
  const [endDate, setEndDate] = useState<Date>(new Date())
  const [period, setPeriod] = useState<string>('')
  const [availableSellers, setAvailableSellers] = useState<Array<{id: string; name: string}>>([])
  
  // Notification dialog
  const [notificationDialog, setNotificationDialog] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState('')
  const [notificationSuccess, setNotificationSuccess] = useState<string | null>(null)

  // Load sellers list
  useEffect(() => {
    loadAvailableSellers()
  }, [])

  // Load data
  useEffect(() => {
    if (period !== '' && selectedSeller !== '') {
      loadData()
    } else {
      // Clear data when filters are not selected
      setSellers([])
      setSellerPerformance(null)
      setLoading(false)
    }
  }, [startDate, endDate, selectedSeller, period])

  const loadAvailableSellers = async () => {
    try {
      const sellersQuery = query(
        collection(db, 'users'),
        where('type', '==', 'seller')
      )
      const snapshot = await getDocs(sellersQuery)
      const sellersList = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || 'Unknown'
      }))
      setAvailableSellers(sellersList)
    } catch (error) {
      console.error('Error loading sellers:', error)
    }
  }

  const loadData = async () => {
    if (period === '' || selectedSeller === '') {
      return
    }
    
    try {
      setLoading(true)
      
      if (selectedSeller === 'all') {
        // Load all sellers summary
        const sellersData = await getAllSellersPerformance(startDate, endDate)
        setSellers(sellersData)
        setSellerPerformance(null)
      } else {
        // Load specific seller details
        const performance = await getSellerPerformance(selectedSeller, startDate, endDate)
        setSellerPerformance(performance)
        setSellers([])
      }
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod)
    const now = new Date()
    
    switch (newPeriod) {
      case '':
        // Reset to default dates when "none" is selected
        setStartDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
        setEndDate(new Date())
        break
      case 'today':
        setStartDate(new Date(now.setHours(0, 0, 0, 0)))
        setEndDate(new Date())
        break
      case '7days':
        setStartDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
        setEndDate(new Date())
        break
      case '30days':
        setStartDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
        setEndDate(new Date())
        break
      case 'thisMonth':
        setStartDate(new Date(now.getFullYear(), now.getMonth(), 1))
        setEndDate(new Date())
        break
      case 'custom':
        // User will set dates manually
        break
    }
  }

  const handleExportCSV = () => {
    if (sellers.length > 0) {
      exportToCSV(
        sellers,
        `sellers_performance_${new Date().toISOString().split('T')[0]}`,
        ['sellerName', 'totalOffers', 'acceptanceRate', 'averageResponseTime', 'averageRating', 'totalRevenue', 'status']
      )
    }
  }

  const handleExportPDF = async (sellerId?: string) => {
    try {
      if (sellerId) {
        const performance = await getSellerPerformance(sellerId, startDate, endDate)
        generateSellerAnalyticsPDF(
          performance,
          `seller_${performance.sellerName}_${new Date().toISOString().split('T')[0]}`
        )
      } else if (sellerPerformance) {
        generateSellerAnalyticsPDF(
          sellerPerformance,
          `seller_${sellerPerformance.sellerName}_${new Date().toISOString().split('T')[0]}`
        )
      }
    } catch (error) {
      console.error('Error exporting PDF:', error)
    }
  }

  const handleSendNotification = async () => {
    try {
      // In a real implementation, this would call a Cloud Function
      // For now, just simulate success
      console.log('Sending notification:', notificationMessage)
      setNotificationSuccess('تم إرسال الإشعار بنجاح')
      setTimeout(() => {
        setNotificationDialog(false)
        setNotificationMessage('')
        setNotificationSuccess(null)
      }, 2000)
    } catch (error) {
      console.error('Error sending notification:', error)
    }
  }

  // Calculate aggregate KPIs
  const aggregateKPIs = sellers.length > 0 ? {
    totalOffers: sellers.reduce((sum, s) => sum + s.totalOffers, 0),
    avgAcceptanceRate: sellers.reduce((sum, s) => sum + s.acceptanceRate, 0) / sellers.length,
    avgResponseTime: sellers.reduce((sum, s) => sum + s.averageResponseTime, 0) / sellers.length,
    avgRating: sellers.reduce((sum, s) => sum + s.averageRating, 0) / sellers.length,
    totalRevenue: sellers.reduce((sum, s) => sum + s.totalRevenue, 0)
  } : null

  // Prepare chart data for lost deals reasons
  const lostDealsChartData = sellerPerformance
    ? Object.entries(sellerPerformance.lostDealsReasons).map(([reason, count]) => ({
        name: reason,
        value: count
      }))
    : []

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight="bold">
            تحليل أداء التجار
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadData}
              disabled={loading}
            >
              تحديث
            </Button>
            {selectedSeller !== '' && selectedSeller !== 'all' && sellerPerformance && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={() => handleExportPDF()}
                >
                  تصدير PDF
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Notifications />}
                  onClick={() => setNotificationDialog(true)}
                >
                  إرسال إشعار
                </Button>
              </>
            )}
            {selectedSeller === 'all' && sellers.length > 0 && (
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={handleExportCSV}
              >
                تصدير CSV
              </Button>
            )}
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>التاجر</InputLabel>
                <Select
                  value={selectedSeller}
                  label="التاجر"
                  onChange={(e) => setSelectedSeller(e.target.value)}
                >
                  <MenuItem value="">لا شيء</MenuItem>
                  <MenuItem value="all">جميع التجار</MenuItem>
                  {availableSellers.map(seller => (
                    <MenuItem key={seller.id} value={seller.id}>
                      {seller.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>الفترة</InputLabel>
                <Select
                  value={period}
                  label="الفترة"
                  onChange={(e) => handlePeriodChange(e.target.value)}
                >
                  <MenuItem value="">لا شيء</MenuItem>
                  <MenuItem value="today">اليوم</MenuItem>
                  <MenuItem value="7days">آخر 7 أيام</MenuItem>
                  <MenuItem value="30days">آخر 30 يوم</MenuItem>
                  <MenuItem value="thisMonth">هذا الشهر</MenuItem>
                  <MenuItem value="custom">مخصص</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {period === 'custom' && (
              <>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="من تاريخ"
                    value={startDate}
                    onChange={(date) => date && setStartDate(date)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="إلى تاريخ"
                    value={endDate}
                    onChange={(date) => date && setEndDate(date)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {period === '' || selectedSeller === '' ? (
          <Box sx={{ py: 4 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              {period === '' && selectedSeller === '' 
                ? 'يرجى اختيار التاجر والفترة من القوائم المنسدلة أعلاه لعرض البيانات'
                : period === ''
                ? 'يرجى اختيار الفترة من القائمة المنسدلة أعلاه لعرض البيانات'
                : 'يرجى اختيار التاجر من القائمة المنسدلة أعلاه لعرض البيانات'}
            </Alert>
          </Box>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {/* KPI Cards */}
            {selectedSeller === 'all' && aggregateKPIs && (
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                  <KPICard
                    title="إجمالي العروض"
                    value={aggregateKPIs.totalOffers.toLocaleString()}
                    icon={<Assessment />}
                    color="#2196F3"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                  <KPICard
                    title="معدل القبول"
                    value={`${aggregateKPIs.avgAcceptanceRate.toFixed(1)}%`}
                    icon={<CheckCircle />}
                    color="#4CAF50"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                  <KPICard
                    title="متوسط وقت الرد"
                    value={`${aggregateKPIs.avgResponseTime.toFixed(0)} دقيقة`}
                    icon={<Timer />}
                    color="#FF9800"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                  <KPICard
                    title="متوسط التقييم"
                    value={`${aggregateKPIs.avgRating.toFixed(2)} / 5.0`}
                    icon={<Star />}
                    color="#FFC107"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                  <KPICard
                    title="إجمالي الإيرادات"
                    value={`$${aggregateKPIs.totalRevenue.toFixed(2)}`}
                    icon={<TrendingUp />}
                    color="#FF6600"
                  />
                </Grid>
              </Grid>
            )}

            {sellerPerformance && (
              <>
                {/* Seller-specific KPIs */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <KPICard
                      title="معدل القبول"
                      value={`${sellerPerformance.acceptanceRate.toFixed(1)}%`}
                      icon={<CheckCircle />}
                      color="#4CAF50"
                      subtitle={`${sellerPerformance.acceptedOffers} من ${sellerPerformance.totalOffers}`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <KPICard
                      title="معدل الإتمام"
                      value={`${sellerPerformance.completionRate.toFixed(1)}%`}
                      icon={<TrendingUp />}
                      color="#2196F3"
                      subtitle={`${sellerPerformance.completedOrders} طلب`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <KPICard
                      title="وقت الرد"
                      value={`${sellerPerformance.averageResponseTime.toFixed(0)} دقيقة`}
                      icon={<Timer />}
                      color="#FF9800"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <KPICard
                      title="التقييم"
                      value={`${sellerPerformance.averageRating.toFixed(2)} / 5.0`}
                      icon={<Star />}
                      color="#FFC107"
                      subtitle={`${sellerPerformance.totalRatings} تقييم`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <KPICard
                      title="درجة الأداء"
                      value={`${sellerPerformance.performanceScore}/100`}
                      icon={<Assessment />}
                      color="#9C27B0"
                    />
                  </Grid>
                </Grid>

                {/* Charts */}
                {lostDealsChartData.length > 0 && (
                  <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      أسباب الصفقات الضائعة
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={lostDealsChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(props: any) => `${props.name} ${((props.percent || 0) * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {lostDealsChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                )}
              </>
            )}

            {/* Sellers Table */}
            {selectedSeller === 'all' && sellers.length > 0 && (
              <SellersTable
                sellers={sellers}
                loading={loading}
                onViewDetails={(sellerId) => setSelectedSeller(sellerId)}
                onExport={(sellerId) => handleExportPDF(sellerId)}
              />
            )}
          </>
        )}

        {/* Notification Dialog */}
        <Dialog open={notificationDialog} onClose={() => setNotificationDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>إرسال إشعار للتاجر</DialogTitle>
          <DialogContent>
            {notificationSuccess ? (
              <Alert severity="success">{notificationSuccess}</Alert>
            ) : (
              <TextField
                fullWidth
                multiline
                rows={4}
                label="الرسالة"
                value={notificationMessage}
                onChange={(e) => setNotificationMessage(e.target.value)}
                sx={{ mt: 2 }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNotificationDialog(false)}>إلغاء</Button>
            <Button
              variant="contained"
              onClick={handleSendNotification}
              disabled={!notificationMessage.trim() || !!notificationSuccess}
            >
              إرسال
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  )
}

